import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MessageComposer from "../../../../components/chat/messageComposer/MessageComposer";
import CheckCircleIcon from "../../../../components/icons/CheckCircleIcon";
import CopiedIcon from "../../../../components/icons/CopiedIcon";
import CopyIcon from "../../../../components/icons/CopyIcon";
import RefreshIcon from "../../../../components/icons/RefreshIcon";
import Loader from "../../../../components/loader/Loader";
import PrimeBadge from "../../../../components/primeBadge/PrimeBadge";
import UserAvatar from "../../../../components/userAvatar/UserAvatar";
import {
  markTicketReadByAdmin,
  queueTicketMessage,
  setTicketStatus,
} from "../../../../features/supportTickets/supportTicketsSlice";
import { getSocket } from "../../../../sockets/webSocketService";
import { formatFullStamp, formatListStamp } from "../../../../utils/chatTime";
import StatusPill from "../statusPill/StatusPill";
import { categoryLabel } from "../ticketMeta";
import TicketThread from "./ticketThread/TicketThread";
import styles from "./ticketWindow.module.css";

const MAX_ATTACHMENTS = 3; // per message (TicketMessage.MAX_ATTACHMENTS)

// The ticket code, copied on click.
const CopyCode = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard refused (no permission): nothing to show.
    }
  };

  return (
    <button
      type="button"
      className={styles.code}
      onClick={copy}
      title={copied ? "تم النسخ" : "نسخ رمز التذكرة"}
      aria-label={copied ? "تم النسخ" : `نسخ رمز التذكرة ${code}`}
    >
      <span dir="ltr">{code}</span>
      {copied ? <CopiedIcon className={styles.codeIcon} /> : <CopyIcon className={styles.codeIcon} />}
    </button>
  );
};

const Stars = ({ rating }) => (
  <span className={styles.stars} aria-label={`${rating} من 5`}>
    {[1, 2, 3, 4, 5].map((star) => (
      <span key={star} className={star <= rating ? styles.starOn : styles.starOff}>
        ★
      </span>
    ))}
  </span>
);

const TicketWindow = () => {
  const dispatch = useDispatch();
  const ticketId = useSelector((state) => state.supportTickets.activeTicketId);
  // The list's copy is the freshest; the opened copy keeps the window up when
  // the filters leave the ticket out of the list.
  const listed = useSelector((state) =>
    state.supportTickets.tickets.find((ticket) => ticket._id === state.supportTickets.activeTicketId),
  );
  const opened = useSelector((state) => state.supportTickets.activeTicket);
  const ticket = listed || opened;
  const savingStatus = useSelector((state) =>
    Boolean(state.supportTickets.statusPending[state.supportTickets.activeTicketId]),
  );
  const senderName = useSelector((state) => state.user.userName);
  const { rolePermissions } = useSelector((state) => state.user);
  const timeZone = useSelector((state) => state.timeZone.timeZone);
  const canReply = rolePermissions.includes("reply-support-tickets");
  const canChangeStatus = rolePermissions.includes("change-ticket-status");

  useEffect(() => {
    if (ticketId) dispatch(markTicketReadByAdmin(ticketId));
  }, [ticketId, dispatch]);

  // "Support is typing" for the player (the app shows it).
  const handleTyping = useCallback(() => {
    getSocket()?.emit("ticket:typing", { ticketId });
  }, [ticketId]);
  const handleStopTyping = useCallback(() => {
    getSocket()?.emit("ticket:stopTyping", { ticketId });
  }, [ticketId]);

  if (!ticket) return null;

  const player = ticket.userId;
  const resolved = ticket.status === "resolved";
  const openedAt = ticket.createdAt ? new Date(ticket.createdAt) : null;

  const toggleStatus = () => {
    if (savingStatus) return;
    dispatch(setTicketStatus({ ticketId, status: resolved ? "open" : "resolved" }));
  };

  let statusLabel = resolved ? "إعادة فتح التذكرة" : "إغلاق التذكرة";
  if (savingStatus) statusLabel = resolved ? "جارٍ إعادة الفتح…" : "جارٍ الإغلاق…";

  return (
    <div className={styles.window}>
      <header className={styles.header}>
        <div className={styles.identity}>
          <span className={styles.avatar}>
            <UserAvatar src={player?.userImage?.path} alt="" />
          </span>
          <div className={styles.identityText}>
            <h2 className={styles.subject} dir="auto">
              {ticket.subject}
            </h2>
            <div className={styles.meta}>
              <StatusPill status={ticket.status} />
              <span className={styles.player} dir="auto">
                {player?.name || "لاعب"}
              </span>
              <PrimeBadge isPrime={player?.isPrime} primeTier={player?.primeTier} size="small" />
              <span className={styles.sep}>·</span>
              <span>{categoryLabel(ticket.category)}</span>
              {ticket.code && (
                <>
                  <span className={styles.sep}>·</span>
                  <CopyCode code={ticket.code} />
                </>
              )}
              {openedAt && (
                <>
                  <span className={styles.sep}>·</span>
                  <span title={formatFullStamp(openedAt, timeZone)}>
                    فُتحت {formatListStamp(openedAt, timeZone)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {canChangeStatus && (
          <button
            type="button"
            className={`${styles.statusButton} ${resolved ? "" : styles.resolveButton}`}
            onClick={toggleStatus}
            disabled={savingStatus}
            aria-busy={savingStatus}
          >
            {savingStatus ? (
              <Loader size={14} />
            ) : resolved ? (
              <RefreshIcon className={styles.statusIcon} />
            ) : (
              <CheckCircleIcon className={styles.statusIcon} />
            )}
            <span>{statusLabel}</span>
          </button>
        )}
      </header>

      {ticket.rating ? (
        <div className={styles.rating}>
          <span className={styles.ratingLabel}>تقييم اللاعب</span>
          <Stars rating={ticket.rating} />
          {ticket.ratingFeedback ? (
            <span className={styles.feedback} dir="auto">
              «{ticket.ratingFeedback}»
            </span>
          ) : null}
        </div>
      ) : null}

      <div className={styles.thread}>
        <TicketThread key={ticketId} ticketId={ticketId} />
      </div>

      {canReply ? (
        <MessageComposer
          key={ticketId}
          draftKey={`ticket:${ticketId}`}
          maxFiles={MAX_ATTACHMENTS}
          notice={resolved ? "هذه التذكرة مغلقة، وردّك سيعيد فتحها بانتظار ردّ اللاعب." : ""}
          onSend={({ text, files }) =>
            dispatch(queueTicketMessage({ ticketId, text, files, senderName }))
          }
          onTyping={handleTyping}
          onStopTyping={handleStopTyping}
        />
      ) : (
        <div className={styles.readOnly}>
          يمكنك قراءة هذه التذكرة فقط؛ الرد يحتاج صلاحية الرد على التذاكر.
        </div>
      )}
    </div>
  );
};

export default TicketWindow;
